import { UserRole } from 'lib_ts/enums/auth.enums';

// todo: rearrange some of the top-level flags to make more sense, maybe grouped by section of app impacted
export interface IUserRoleFeatureFlags {
  // e.g. team or super
  isAnyAdmin?: boolean;
  // e.g. only super
  isSuperAdmin?: boolean;
  r2fInspector?: boolean;
  pitchMsDetails?: boolean;
  healthCheck?: boolean;
  machineDebugger?: boolean;
  // from connected machine menu
  repeatability?: boolean;
  impersonation?: boolean;
  videoMiscDetails?: boolean;
  gameDataActions?: boolean;
  controls: {
    superMode?: boolean;
    downloadVideo?: boolean;
    cameraSources?: boolean;
    inserterCameraCrosshair?: boolean;
    rapidMode?: boolean;
    switchMachine?: boolean;
    plateViewExtras?: boolean;
    deceptionMode?: boolean;
  };
  manage: {
    pitchListType?: boolean;
  };
  sections: {
    hitterLibrary?: boolean;
    videoLibrary?: boolean;
    analytics?: boolean;
    adminApiKeys?: boolean;
    adminBallTypes?: boolean; // to remove
    adminErrorTypes?: boolean;
    adminMachineModels?: boolean;
    adminPortal?: boolean;
    adminServerControls?: boolean;
    adminServerErrors?: boolean; // to remove
    adminStaticVideos?: boolean;
    adminTeams?: boolean;
    featureDemo?: boolean;
  };
  adminModels: {
    update?: boolean;
  };
  adminMachines: {
    ampCalibration?: boolean;
    autoUpdateModel?: boolean;
    burnInTests?: boolean;
    changeStrikeZoneType?: boolean;
    create?: boolean;
    createTailscaleKey?: boolean;
    daemonStates?: boolean;
    fwLogs?: boolean;
    hwDiagnostics?: boolean;
    install?: boolean;
    liveFires?: boolean;
    pitchReport?: boolean;
    onDemandCalibrationRebuild?: boolean;
    onDemandCalibrationReset?: boolean;
    plotting?: boolean;
    projectorReset?: boolean;
    projectorShutdown?: boolean;
    relayRestart?: boolean;
    restartFirmware?: boolean;
    restoreLists?: boolean;
    serviceLogs?: boolean;
    shotHistory?: boolean;
    tiltLutReset?: boolean;
    toggleDeleted?: boolean;
    trainingPerformance?: boolean;
    upgradeFirmware?: boolean;
    ventionCalibration?: boolean;
  };
  adminTeams: {
    create?: boolean;
    permissions?: boolean;
    restoreLists?: boolean;
    statsApiHitterImport?: boolean; // whether to allow enabling of Stats API hitter sync from hitter library
    update?: boolean;
  };
  adminUsers: {
    create?: boolean;
    delete?: boolean;
    restoreLists?: boolean;
    viewSessions?: boolean;
  };
  adminMisc: {
    genCRUDKey?: boolean;
  };
  analytics: {
    exportStats?: boolean;
    visualize?: boolean;
  };
}

export const DEFAULT_FEATURE_FLAGS: IUserRoleFeatureFlags = {
  sections: {},
  manage: {},
  controls: {},
  adminMachines: {},
  analytics: {},
  adminUsers: {},
  gameDataActions: true,
  adminModels: {},
  adminTeams: {},
  adminMisc: {},
};

export const BASIC_USER_FEATURE_FLAGS: IUserRoleFeatureFlags = {
  sections: {
    hitterLibrary: true,
    videoLibrary: true,
    analytics: true,
  },
  manage: {},
  controls: {},
  adminMachines: {},
  analytics: {},
  adminUsers: {},
  gameDataActions: true,
  adminModels: {},
  adminTeams: {},
  adminMisc: {},
};

export const RESTRICTED_BASIC_USER_FEATURE_FLAGS: IUserRoleFeatureFlags = {
  sections: {},
  manage: {},
  controls: {},
  adminMachines: {},
  analytics: {},
  adminUsers: {},
  adminModels: {},
  adminTeams: {},
  adminMisc: {},
};

export const TEAM_ADMIN_FEATURE_FLAGS: IUserRoleFeatureFlags = {
  isAnyAdmin: true,
  sections: {
    adminPortal: true,
    adminApiKeys: true,
    hitterLibrary: true,
    videoLibrary: true,
    analytics: true,
  },
  manage: {},
  controls: {
    switchMachine: true,
  },
  adminMachines: {},
  analytics: {},
  adminUsers: {},
  gameDataActions: true,
  adminModels: {},
  adminTeams: {
    statsApiHitterImport: true,
  },
  adminMisc: {},
};

// during a game
export const RESTRICTED_TEAM_ADMIN_FEATURE_FLAGS: IUserRoleFeatureFlags = {
  isAnyAdmin: true,
  sections: {},
  manage: {},
  controls: {
    switchMachine: true,
  },
  adminMachines: {},
  analytics: {},
  adminUsers: {},
  adminModels: {},
  adminTeams: {},
  adminMisc: {},
};

export const SUPER_ADMIN_FEATURE_FLAGS: IUserRoleFeatureFlags = {
  isAnyAdmin: true,
  isSuperAdmin: true,
  r2fInspector: true,
  pitchMsDetails: true,
  healthCheck: true,
  machineDebugger: true,
  repeatability: true,
  impersonation: true,
  videoMiscDetails: true,
  manage: {
    pitchListType: true,
  },
  sections: {
    adminBallTypes: true,
    adminErrorTypes: true,
    adminMachineModels: true,
    adminPortal: true,
    adminServerControls: true,
    adminServerErrors: true,
    adminStaticVideos: true,
    adminTeams: true,
    featureDemo: true,
    adminApiKeys: true,
    hitterLibrary: true,
    videoLibrary: true,
    analytics: true,
  },
  controls: {
    superMode: true,
    downloadVideo: true,
    cameraSources: true,
    inserterCameraCrosshair: true,
    rapidMode: true,
    switchMachine: true,
    plateViewExtras: true,
    deceptionMode: true,
  },
  adminMachines: {
    ampCalibration: true,
    autoUpdateModel: true,
    burnInTests: true,
    changeStrikeZoneType: true,
    create: true,
    createTailscaleKey: true,
    daemonStates: true,
    fwLogs: true,
    hwDiagnostics: true,
    install: true,
    toggleDeleted: true,
    liveFires: true,
    onDemandCalibrationRebuild: true,
    onDemandCalibrationReset: true,
    pitchReport: true,
    plotting: true,
    projectorReset: true,
    projectorShutdown: true,
    relayRestart: true,
    restartFirmware: true,
    restoreLists: true,
    serviceLogs: true,
    shotHistory: true,
    tiltLutReset: true,
    trainingPerformance: true,
    upgradeFirmware: true,
    ventionCalibration: true,
  },
  analytics: {
    exportStats: true,
    visualize: true,
  },
  adminUsers: {
    create: true,
    delete: true,
    restoreLists: true,
    viewSessions: true,
  },
  gameDataActions: true,
  adminModels: {
    update: true,
  },
  adminTeams: {
    create: true,
    permissions: true,
    restoreLists: true,
    statsApiHitterImport: true,
    update: true,
  },
  adminMisc: {
    genCRUDKey: true,
  },
};

export const getUserFeatureFlags = (
  role: UserRole,
  isInGame: boolean
): IUserRoleFeatureFlags => {
  switch (role) {
    case UserRole.basic: {
      if (isInGame) {
        return RESTRICTED_BASIC_USER_FEATURE_FLAGS;
      }

      return BASIC_USER_FEATURE_FLAGS;
    }

    case UserRole.team_admin: {
      if (isInGame) {
        return RESTRICTED_TEAM_ADMIN_FEATURE_FLAGS;
      }

      return TEAM_ADMIN_FEATURE_FLAGS;
    }

    case UserRole.admin: {
      return SUPER_ADMIN_FEATURE_FLAGS;
    }

    default: {
      return DEFAULT_FEATURE_FLAGS;
    }
  }
};
